import React, { useEffect, useState } from "react";
import { Sparkles, Trophy, Star, Award, Crown } from "lucide-react";
import api from "@/lib/api";

const LEVEL_STYLES = {
  1: { bg: "bg-[#F3F1ED]", text: "text-[#4B5563]", border: "border-[#EAE7E0]", icon: Sparkles }, // Wali Baru — neutral
  2: { bg: "bg-[#EFF6FF]", text: "text-[#1E3A8A]", border: "border-[#DBEAFE]", icon: Star },      // Orang Tua Aktif — blue
  3: { bg: "bg-[#F0FDF4]", text: "text-[#166534]", border: "border-[#BBF7D0]", icon: Award },     // Kontributor — green
  4: { bg: "bg-[#FEF3C7]", text: "text-[#78350F]", border: "border-[#FCD34D]", icon: Trophy },    // Pakar — gold
  5: { bg: "bg-[#FDF2F8]", text: "text-[#831843]", border: "border-[#F9A8D4]", icon: Crown },     // Legend — magenta
};

// Module-level cache — one HTTP request per page load feeds every badge on screen.
// Set by fetchGamificationEnabled() below; also updated by the Admin toggle
// via the `gamification:changed` custom event so no page reload is needed.
let _gamEnabled = null;   // null=unknown, true=on, false=off
let _pending = null;      // in-flight promise dedupe
const listeners = new Set();

function _notify() { listeners.forEach((cb) => cb(_gamEnabled)); }

async function fetchGamificationEnabled() {
  if (_gamEnabled !== null) return _gamEnabled;
  if (_pending) return _pending;
  _pending = api.get("/level-config").then((r) => {
    _gamEnabled = r.data?.enabled === false ? false : true;
    _notify();
    return _gamEnabled;
  }).catch(() => {
    _gamEnabled = true; // fail-open so legacy behaviour is preserved on network error
    _notify();
    return _gamEnabled;
  }).finally(() => { _pending = null; });
  return _pending;
}

// Public setter for the Admin toggle — flips the cache and notifies every mounted badge.
export function setGamificationEnabled(v) {
  _gamEnabled = !!v;
  _notify();
  try { window.dispatchEvent(new CustomEvent("gamification:changed", { detail: _gamEnabled })); } catch {}
}

/**
 * Small inline level badge shown next to username throughout the app.
 * Renders NULL when gamification is globally disabled by admin —
 * the underlying contribution counting on the backend still runs.
 * Props: name (string), order (1..5), size ("xs"|"sm"), testid
 */
export default function UserLevelBadge({ name, order = 1, size = "xs", testid }) {
  const [enabled, setEnabled] = useState(_gamEnabled);

  useEffect(() => {
    // Trigger fetch once + subscribe to changes
    if (_gamEnabled === null) fetchGamificationEnabled().then(setEnabled);
    const cb = (v) => setEnabled(v);
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);

  if (!name) return null;
  if (enabled === false) return null; // globally disabled by admin

  const s = LEVEL_STYLES[order] || LEVEL_STYLES[1];
  const Icon = s.icon;
  const sizing = size === "sm" ? "text-[11px] px-2 py-0.5" : "text-[10px] px-1.5 py-0.5";
  return (
    <span
      data-testid={testid || `user-level-${order}`}
      className={`inline-flex items-center gap-1 rounded-full font-semibold uppercase tracking-wider border ${sizing} ${s.bg} ${s.text} ${s.border}`}
      title={`Level ${order}: ${name}`}
    >
      <Icon size={size === "sm" ? 11 : 10} />
      <span>{name}</span>
    </span>
  );
}
